import type { ReadonlyURLSearchParams } from "$app/state";
import {
  DEFAULT_PRESET,
  PRESETS,
  intervalFor,
  presetSince,
  shiftDays,
} from "@colossus/contracts/analytics-scope";
import type { Preset, QueryScope } from "@colossus/contracts/analytics-scope";
import { BUCKET_INTERVALS, boundInstantMs, isRangeBound } from "@colossus/contracts/buckets";
import { m } from "./paraglide/messages.js";
import type { Message } from "./locale.ts";

const INTERVALS = BUCKET_INTERVALS;

export type Interval = (typeof INTERVALS)[number];

export {
  DEFAULT_PRESET,
  PRESETS,
  channelsQuery,
  intervalFor,
  isoDay,
  maturedWindow,
  presetSince,
  rolesQuery,
  shiftDays,
} from "@colossus/contracts/analytics-scope";
export type { Preset } from "@colossus/contracts/analytics-scope";

export const PRESET_LABEL = {
  "24h": m.preset_24h,
  "7d": m.preset_7d,
  "30d": m.preset_30d,
  "90d": m.preset_90d,
  "12m": m.preset_12m,
} as const satisfies Record<Preset, Message>;

export const LEADERBOARD_METRICS = [
  "messages",
  "voice",
  "reactions_given",
  "reactions_received",
] as const;

type LeaderboardMetric = (typeof LEADERBOARD_METRICS)[number];

export const PAIR_KINDS = ["replies", "mentions"] as const;

export type PairKind = (typeof PAIR_KINDS)[number];

export interface Scope extends QueryScope {
  readonly preset: Preset | null;
  readonly interval: Interval;
  readonly channelId: string | null;
  readonly compareChannelId: string | null;
  readonly userId: string | null;
  readonly compareUserId: string | null;
  readonly metric: LeaderboardMetric;
  readonly pairs: PairKind;
  readonly everything: boolean;
  readonly floor: string | null;
  readonly capturedFrom: string | null;
}

const PRESET_ROLLING_MS: Partial<Record<Preset, number>> = {
  "24h": 24 * 60 * 60 * 1000,
};

const SNOWFLAKE = /^\d{1,25}$/;

export const reachesKept = (dataSince: string, today: string, kept: number | null): boolean =>
  kept !== null && dataSince <= shiftDays(today, 1 - kept);

const presetRange = (
  today: string,
  now: Date,
  preset: Preset,
): { readonly since: string; readonly until: string } => {
  const rolling = PRESET_ROLLING_MS[preset];
  if (rolling === undefined) return { since: presetSince(today, preset), until: today };
  return { since: new Date(now.getTime() - rolling).toISOString(), until: now.toISOString() };
};

const oneOf = <T extends string>(values: readonly T[], raw: string | null, whenAbsent: T): T => {
  if (raw === null) return whenAbsent;
  const found = values.find((value) => value === raw);
  return found === undefined ? whenAbsent : found;
};

const idList = (raw: string | null): readonly string[] =>
  raw === null ? [] : raw.split(",").filter((id) => SNOWFLAKE.test(id));

const id = (raw: string | null): string | null =>
  raw !== null && SNOWFLAKE.test(raw) ? raw : null;

const bound = (raw: string | null): string | null =>
  raw !== null && isRangeBound(raw) ? raw : null;

export type ScopeFallback = Preset | "all";

interface ScopeSource {
  readonly params: ReadonlyURLSearchParams;
  readonly guildId: string;
  readonly today: string;
  readonly now?: Date;
  readonly fallback?: ScopeFallback | undefined;
  readonly recorded?: { readonly since: string | null; readonly until: string | null };
  readonly floor?: string | null;
  readonly capturedFrom?: string | null;
}

const openedOnAll = (
  source: ScopeSource,
): { readonly since: string; readonly until: string } | null => {
  const { params, recorded } = source;
  if (source.fallback !== "all" || recorded === undefined) return null;
  if (TIME_PARAMS.some((name) => params.has(name))) return null;
  const { since, until } = recorded;
  return since === null || until === null ? null : { since, until };
};

export const readScope = (source: ScopeSource): Scope => {
  const { params, today } = source;
  const explicitSince = bound(params.get("since"));
  const explicitUntil = bound(params.get("until"));
  const all = openedOnAll(source);
  const custom =
    explicitSince !== null &&
    explicitUntil !== null &&
    boundInstantMs(explicitSince, "since") < boundInstantMs(explicitUntil, "until");
  const fallback =
    source.fallback === undefined || source.fallback === "all" ? DEFAULT_PRESET : source.fallback;
  const preset = custom || all !== null ? null : oneOf(PRESETS, params.get("preset"), fallback);
  const fromPreset = presetRange(
    today,
    source.now ?? new Date(),
    preset === null ? DEFAULT_PRESET : preset,
  );
  const window =
    all !== null ? all : custom ? { since: explicitSince, until: explicitUntil } : null;
  const until = window === null ? fromPreset.until : window.until;
  const since = window === null ? fromPreset.since : window.since;

  return {
    guildId: source.guildId,
    since,
    until,
    today,
    preset,
    interval: intervalFor(since, until),
    rolesInclude: idList(params.get("rolesInclude")),
    rolesExclude: idList(params.get("rolesExclude")),
    channelsInclude: idList(params.get("channelsInclude")),
    channelsExclude: idList(params.get("channelsExclude")),
    channelId: id(params.get("channel")),
    compareChannelId: id(params.get("channelB")),
    userId: id(params.get("user")),
    compareUserId: id(params.get("userB")),
    metric: oneOf(LEADERBOARD_METRICS, params.get("metric"), "messages"),
    pairs: oneOf(PAIR_KINDS, params.get("pairs"), "replies"),
    everything:
      all !== null ||
      (custom &&
        source.recorded !== undefined &&
        since === source.recorded.since &&
        until === source.recorded.until),
    floor: source.floor ?? null,
    capturedFrom: source.capturedFrom ?? null,
  };
};

export const TIME_PARAMS = ["preset", "since", "until"] as const;

const SCOPE_PARAMS = [
  ...TIME_PARAMS,
  "rolesInclude",
  "rolesExclude",
  "channelsInclude",
  "channelsExclude",
  "channel",
  "channelB",
  "user",
  "userB",
  "metric",
  "pairs",
] as const;

export type ScopeParam = (typeof SCOPE_PARAMS)[number];

export const withParam = (
  params: URLSearchParams,
  name: ScopeParam,
  value: string | readonly string[] | null,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  const flat = typeof value === "string" || value === null ? value : value.join(",");
  if (flat === null || flat === "") next.delete(name);
  else next.set(name, flat);
  if (name === "since" || name === "until") next.delete("preset");
  if (name === "preset") {
    next.delete("since");
    next.delete("until");
  }
  next.sort();
  return next;
};

export const toggleId = (current: readonly string[], value: string): readonly string[] =>
  current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value];

export const filterCount = (scope: Scope): number =>
  scope.rolesInclude.length +
  scope.rolesExclude.length +
  scope.channelsInclude.length +
  scope.channelsExclude.length;
